import { useState, useEffect, useRef } from 'react';
import { audioEngine } from '../features/audio/audioEngine';
import { getNoteFromFrequency } from '../utils/audioUtils.js';

export function useAudio() {
    const [isRunning, setIsRunning] = useState(false);
    const [trackName, setTrackName] = useState("Standby");
    const [pitchData, setPitchData] = useState({ frequency: 0, clarity: 0, note: '-' });
    const [error, setError] = useState(null);
    const rafId = useRef(null);

    const updateLoop = () => {
        if (!audioEngine.isRunning) {
            setIsRunning(false);
            return; // Don't cancel RAF yet, let UI update one last time
        } else {
            setIsRunning(true);
        }

        const { frequency, clarity } = audioEngine.getAnalysis();

        // Only update if clarity is decent
        if (clarity > 0.8) {
            setPitchData({
                frequency,
                clarity,
                note: getNoteFromFrequency(frequency)
            });
        } else {
            // Decay or hold?
        }

        rafId.current = requestAnimationFrame(updateLoop);
    };

    const startEngine = async () => {
        try {
            if (audioEngine.mode === 'mic') {
                await audioEngine.startMic();
            } else {
                audioEngine.play();
            }
            setIsRunning(true);
            updateLoop();
        } catch (err) {
            setError(err.message);
        }
    };

    const stopEngine = () => {
        audioEngine.stop();
        setIsRunning(false);
        if (rafId.current) cancelAnimationFrame(rafId.current);
    };

    // Sync track name periodically or via event?
    useEffect(() => {
        const interval = setInterval(() => {
            if (audioEngine.currentTrackName !== trackName) {
                setTrackName(audioEngine.currentTrackName);
            }
            // Also sync running state more reliably
            if (audioEngine.isRunning !== isRunning) {
                setIsRunning(audioEngine.isRunning);
                if (audioEngine.isRunning && !rafId.current) updateLoop();
            }
        }, 500);
        return () => clearInterval(interval);
    }, [trackName, isRunning]);

    return { isRunning, trackName, pitchData, error, startEngine, stopEngine };
}
